import { EErrorCode } from 'config/errors-codes';

/**
 * Sink for the Session Flight Recorder's error lane (AVS-4428, parent AVS-4423).
 *
 * Same discipline as `setLogSink` in `utils/log.ts` and `setListenerGuardReporter` in
 * `utils/events.ts:61`: a settable module-level function defaulting to a no-op, called from inside
 * the owning class and wrapped in its own try/catch, so a throwing sink can never fail the thing it
 * observes — here, an error constructor.
 *
 * It is deliberately NOT a listener. A listener returning a falsy value participates in the
 * prevent-execution protocol (`utils/events.ts:239`) that `utils/ui/media.ts:144` uses to abort
 * `getUserMedia`.
 */
export type TApiErrorSink = (error: ApiError) => void;

function noopApiErrorSink(): void {
  // Default sink: no recorder installed.
}

let apiErrorSink: TApiErrorSink = noopApiErrorSink;

/** Register the recorder's error sink. Pass `null` to restore the no-op. */
export function setApiErrorSink(sink: TApiErrorSink | null): void {
  apiErrorSink = sink ?? noopApiErrorSink;
}

/** The try block holds only the sink call; a recorder fault must never break error construction. */
function reportToRecorder(error: ApiError): void {
  try {
    apiErrorSink(error);
  } catch {
    // A recorder fault may cost telemetry; it may never cost the error being constructed.
  }
}

export interface IApiErrorOptions {
  statusCode?: number;
  /**
   * Skip the global `window.E3D.reportError` emission. Set when the caller has
   * already decided the failure must be invisible to integrators (e.g. a known-
   * broken backend endpoint behind a feature flag). Pairs with `suppressToast`
   * in `sendApiRequest` for full silence across toast / console / `'error'`-event.
   *
   * It does NOT silence the recorder sink: `silent` means "invisible to integrators", and a
   * failure nobody is told about is exactly the one a flight recorder exists to preserve.
   */
  silent?: boolean;
}

export class ApiError extends Error {
  code: EErrorCode;
  statusCode?: number;

  constructor(code: EErrorCode, message: string, options?: IApiErrorOptions) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = options?.statusCode;

    reportToRecorder(this);

    if (!options?.silent && typeof window !== 'undefined') {
      // report error to the runtime
      window?.E3D?.reportError(this);
    }
  }
}

export type TExternalApiType = 'vsrs' | 'okapi' | 'vtp' | 'oscar' | 'casa' | 'gmc' | 'naap' | 'dga' | 'carlos';

/** The exact property names that contain the error information vary depending on the api. This type contains all possibilities */
export type TExternalApiErrorResponse = {
  status_text?: string;
  error_code?: string;
  error_message?: string;
  error?: string;
  error_description?: string;
  message?: string;
};

export interface IExternalApiErrorOptions extends IApiErrorOptions {
  statusCode: number;
  apiType: TExternalApiType;
  errorData: TExternalApiErrorResponse;
}
export class ExternalApiError extends ApiError {
  statusCode: number;
  apiType: TExternalApiType;
  apiResponse: TExternalApiErrorResponse;

  constructor(code: EErrorCode, options: IExternalApiErrorOptions) {
    const { statusCode, apiType, errorData } = options;
    const message = `${apiType} - ${statusCode} - ${JSON.stringify(errorData)}`;
    super(code, message, { statusCode });
    this.name = 'ExternalApiError';
    this.statusCode = statusCode;
    this.apiType = apiType;
    this.apiResponse = errorData;
  }
}

export class ItemNotFoundError extends Error {}

export class ValidationError extends Error {}
